import { create } from "zustand";
import { ProductType } from "./types";

export interface DesignerObject {
  id: string;
  name: string;
  type: "image" | "text";

  fabricObject?: any;

  visible: boolean;
  locked: boolean;
}

type Side = "front" | "back";

interface DesignerStore {
  // Product
  product: ProductType;
  setProduct: (product: ProductType) => void;

  // T-shirt side (front / back). Gang sheets are single-sided.
  side: Side;
  setSide: (side: Side) => void;
  sideData: { front: string | null; back: string | null };
  setSideData: (side: Side, data: string | null) => void;
  resetSides: () => void;

  // Print mode: onto a garment, or supply the transfer only.
  printMode: "print_on_garment" | "print_only";
  setPrintMode: (mode: "print_on_garment" | "print_only") => void;

  // Chosen garment colour (hex). Tints the shirt preview only; artwork is never
  // recoloured. Saved with the design/order.
  garmentColor: string;
  setGarmentColor: (hex: string) => void;

  // Uploaded logo
  logo: string | null;
  setLogo: (logo: string | null) => void;

  // Selected object
  selectedObject: any | null;
  setSelectedObject: (object: any | null) => void;

  // Selected object size
  selectedWidth: number;
  selectedHeight: number;
  setSelectedSize: (width: number, height: number) => void;

  // Uploaded image information
  imageWidth: number;
  imageHeight: number;
  setImageInfo: (width: number, height: number) => void;

  // Designer Objects
  objects: DesignerObject[];

  addObject: (object: DesignerObject) => void;
  removeObject: (id: string) => void;

  updateObject: (
    id: string,
    updates: Partial<DesignerObject>
  ) => void;

  clearObjects: () => void;
}

export const useDesignerStore = create<DesignerStore>((set) => ({

  // ==========================
  // Product
  // ==========================

  product: "tshirt",

  setProduct: (product) =>
    set({
      product,
      side: "front",
      sideData: { front: null, back: null },
    }),

  // ==========================
  // Sides
  // ==========================

  side: "front",
  setSide: (side) => set({ side }),
  sideData: { front: null, back: null },
  setSideData: (side, data) =>
    set((state) => ({ sideData: { ...state.sideData, [side]: data } })),
  resetSides: () => set({ side: "front", sideData: { front: null, back: null } }),

  printMode: "print_on_garment",
  setPrintMode: (printMode) => set({ printMode }),

  garmentColor: "#ffffff",
  setGarmentColor: (garmentColor) => set({ garmentColor }),

  // ==========================
  // Logo
  // ==========================

  logo: null,

  setLogo: (logo) =>
    set({
      logo,
    }),

  // ==========================
  // Selected Object
  // ==========================

  selectedObject: null,

  setSelectedObject: (object) =>
    set({
      selectedObject: object,
    }),

  // ==========================
  // Selected Size
  // ==========================

  selectedWidth: 0,
  selectedHeight: 0,

  setSelectedSize: (selectedWidth, selectedHeight) =>
    set({
      selectedWidth,
      selectedHeight,
    }),

  // ==========================
  // Image Info
  // ==========================

  imageWidth: 0,
  imageHeight: 0,

  setImageInfo: (imageWidth, imageHeight) =>
    set({
      imageWidth,
      imageHeight,
    }),

  // ==========================
  // Layers
  // ==========================

  objects: [],

  addObject: (object) =>
    set((state) => ({
      objects: [...state.objects, object],
    })),

  removeObject: (id) =>
    set((state) => ({
      objects: state.objects.filter(
        (object) => object.id !== id
      ),
    })),

  updateObject: (id, updates) =>
    set((state) => ({
      objects: state.objects.map((object) =>
        object.id === id
          ? {
              ...object,
              ...updates,
            }
          : object
      ),
    })),

  clearObjects: () =>
    set({
      objects: [],
    }),

}));